import DwinityLoader from "../DwinityLoader";
import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { AppContext } from "../../AppContextProvider";
import moment from "moment";
import { WalletProviderContext } from "../../walletprovider/WalletProvider";
import { ErrorTicket, WarningTicket, NotificationsTicket, InformationsTicket } from "../TicketsComponent";
import { sortBy } from "./sorter";
import FilemanagementComponent from "./FilemanagementComponent";
import FilemanagementComponentDesktop from "./FilemanagementComponentDesktop";
import { graphProcessor, collectContentTypesFromTree, collectTagsFromTree } from "./graphProcessor2";
import { graphProcessorToFileList } from "./graphProcessorFileList";
import { dwReportError, ReportingError } from "../../utils/diagnostics/clientErrors";

import { MDBContainer } from "mdb-react-ui-kit";
import { DwMpcWalletBackendProviderContext } from "../../mpcwalletprovider/DwMpcWalletBackendProvider";
import { unProcessAndSaveProcessedGraphJwt } from "../../utils/graph/graphloader2";
import { removeFile, getDownloadUrl } from "../../utils/dwclient/dwstorage";
import { readFileCached } from "../../utils/previewCache";
import {
  deleteSingle2,
  findNodeId2,
  getAllFolders2,
  findChildrenOfNodeById2,
  RELATION_FILE_FOLDER2,
  TYPE_FILE2,
  TYPE_FOLDER2,
  mkdirs2,
} from "../../utils/graph/graphmodifier2";
import { loadJwt } from "../../utils/dwclient/localstorage/jwtStorage";
import {
  loadSortFilter,
  loadSelectedContentTypeFilters,
  loadSelectedTagFilters,
} from "../../utils/explorer/explorerstate";
import { deleteNodeRecursiveJwt, findFileSystemRoot2 } from "../../utils/graph/graphstorage2";
import { zipDownload } from "../../utils/zipdownload/zipdownloadworkerutil";
import { chromeDownload } from "../../utils/zipdownload/chromedownload";
import { utilSingleFileDownload } from "../../utils/zipdownload/downloadcomponentutil";
import { formatTimeHHMMSS } from "../../utils/paths";

import { pdfjs } from "react-pdf";

// Konstansok kiemelése
const DOWNLOAD_LIMIT_DEFAULT = 15728640;
const DOWNLOAD_LIMIT_MOBILE = 167108836;
const FILE_NAME_MAX_LENGTH = 30;
const FILE_NAME_TRUNCATE_LENGTH = 25;

// PDF.js worker beállítása
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// MIME type mapping objektum a jobb teljesítményért
const MIME_TYPE_MAP = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  txt: "text/plain",
  zip: "application/zip",
  json: "application/json",
  xml: "application/xml",
  csv: "text/csv",
};

// Utility függvények kiemelése a komponensen kívülre
const getMimeTypeFromExtension = (extension) => {
  return MIME_TYPE_MAP[extension?.toLowerCase()] || "application/octet-stream";
};

const isIOSDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

const hasIntersection = (arr1, arr2) => {
  if (!arr1?.length || !arr2?.length) return false;
  const set = new Set(arr1);
  return arr2.some((item) => set.has(item));
};

const isAbortError = (error, signal) =>
  signal?.aborted ||
  error?.name === "AbortError" ||
  error?.name === "CanceledError" ||
  error?.code === "ERR_CANCELED";

const getSessionStorageSelectedNodes = () => {
  const nodeListString = sessionStorage.getItem("selectedNodeList");
  return nodeListString ? JSON.parse(nodeListString) : null;
};

const setSessionStorageSelectedNodes = (nodes) => {
  if (nodes?.length > 0) {
    sessionStorage.setItem("selectedNodeList", JSON.stringify(nodes));
  } else {
    sessionStorage.removeItem("selectedNodeList");
  }
};
export default function ExplorerDataComponent(props) {
  // Refs
  const basicRef = useRef(null);
  const { t } = useTranslation();

  // Context hooks
  const context = useContext(AppContext);
  const { selectedAccount, selectedWallet } = useContext(WalletProviderContext);
  const { storageProps } = useContext(DwMpcWalletBackendProviderContext);

  // State declarations - logikusan csoportosítva
  const [fileList, setFileList] = useState([]);

  // Folder/file operations
  const [folderToRemove, setFolderToRemove] = useState();
  const [actuelleDataOwnerComponent, setActuelleDataOwnerComponent] = useState();

  // Ticket states
  const [isWarningTicket, setIsWarningTicket] = useState(false);
  const [isWarningTicket3, setIsWarningTicket3] = useState(false);
  const [isDoneTicket, setIsDoneTicket] = useState(false);
  const [isErrorTicket, setIsErrorTicket] = useState(false);
  const [showFileOperationFailedTicket, setShowFileOperationFailedTicket] = useState(false);
  const [fileOperationFailedTicketText, setFileOperationFailedTicketText] = useState(undefined);
  const [errortext, setErrortext] = useState(undefined);

  // Download states
  const [isZipDownload, setIsZipDownload] = useState(false);
  const [isFolderDownload, setIsFolderDownload] = useState(false);

  // Progress elements - memoized DOM queries
  const [progressElements, setProgressElements] = useState({
    bar: null,
    size: null,
    timer: null,
    timertext: null,
    filenumber: null,
  });

  const [isVisibility3, setIsVisibility3] = useState(false);
  const [showCloudMenu, setShowCloudMenu] = useState(false);
  const [cloudImporting, setCloudImporting] = useState(false);
  const cloudMenuRef = useRef(null);

  // Constants with fallbacks
  const downloadLimit = useMemo(() => process.env.DOWNLOAD_LIMIT_FOR_PREVIEW ?? DOWNLOAD_LIMIT_DEFAULT, []);

  // Utility functions moved inside component for better context access
  const updateProgressElements = useCallback(() => {
    setProgressElements({
      bar: document.getElementById("progress-bar"),
      size: document.getElementById("file-size"),
      timer: document.getElementById("file-timer"),
      timertext: document.getElementById("file-timer-text"),
      filenumber: document.getElementById("file-number"),
    });
  }, []);

  const showErrorTicket = useCallback((message) => {
    setFileOperationFailedTicketText(message);
    setShowFileOperationFailedTicket(true);
  }, []);

  useEffect(() => {
    if (!showCloudMenu) return;
    const handleClickOutside = (e) => {
      if (cloudMenuRef.current && !cloudMenuRef.current.contains(e.target)) {
        setShowCloudMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCloudMenu]);

  const doCloudImport = useCallback((fileObjects) => {
    context.openModal("fileupload", "", {
      processedGraph: props.loadedGraph,
      onFinish: props.refreshGraph,
      preloadedFiles: fileObjects,
      onError: (errorString) => {
        context.closeModal();
        setIsErrorTicket(true);
        setErrortext(errorString);
      },
    });
  }, [context, props.loadedGraph, props.refreshGraph]);

  const handleDropboxImport = useCallback(() => {
    setShowCloudMenu(false);
    if (typeof window.Dropbox === "undefined" || !window.Dropbox.choose) {
      console.error("[CloudImport] Dropbox Chooser SDK not loaded");
      return;
    }
    window.Dropbox.choose({
      success: (files) => {
        if (!files || files.length === 0) return;
        setCloudImporting(true);
        Promise.all(
          files.map((f) =>
            fetch(f.link)
              .then((res) => res.blob())
              .then((blob) => new File([blob], f.name, { type: blob.type || "application/octet-stream" }))
          )
        )
          .then((fileObjects) => doCloudImport(fileObjects))
          .catch((err) => console.error("[CloudImport] Dropbox download error:", err))
          .finally(() => setCloudImporting(false));
      },
      cancel: () => console.log("[CloudImport] Dropbox chooser cancelled"),
      linkType: "direct",
      multiselect: true,
      folderselect: false,
    });
  }, [doCloudImport]);

  const handleGoogleDriveImport = useCallback(() => {
    setShowCloudMenu(false);
    const apiKey = process.env.GOOGLE_PICKER_API_KEY;
    const clientId = process.env.GOOGLE_OAUTH_CLIENT_ID;
    const appId = process.env.GOOGLE_APP_ID;
    if (!apiKey || !clientId) {
      console.error("[CloudImport] Google API credentials not configured");
      return;
    }
    const loadGis = () => new Promise((resolve) => {
      if (window.google?.accounts?.oauth2) { resolve(); return; }
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
    const loadPicker = () => new Promise((resolve) => {
      if (window.google?.picker) { resolve(); return; }
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.onload = () => { window.gapi.load("picker", () => resolve()); };
      document.head.appendChild(script);
    });
    Promise.all([loadGis(), loadPicker()])
      .then(() => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "https://www.googleapis.com/auth/drive.readonly",
          callback: (tokenResponse) => {
            if (!tokenResponse?.access_token) return;
            const picker = new window.google.picker.PickerBuilder()
              .setAppId(appId || "")
              .setOAuthToken(tokenResponse.access_token)
              .setDeveloperKey(apiKey)
              .addView(window.google.picker.ViewId.DOCS)
              .setCallback((data) => {
                if (data.action !== window.google.picker.Action.PICKED) return;
                setCloudImporting(true);
                Promise.all(
                  data.docs.map((doc) =>
                    fetch(
                      `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`,
                      { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
                    )
                      .then((res) => res.blob())
                      .then((blob) => new File([blob], doc.name, { type: doc.mimeType || "application/octet-stream" }))
                  )
                )
                  .then((fileObjects) => doCloudImport(fileObjects))
                  .catch((err) => console.error("[CloudImport] Google Drive download error:", err))
                  .finally(() => setCloudImporting(false));
              })
              .build();
            picker.setVisible(true);
          },
        });
        tokenClient.requestAccessToken();
      })
      .catch((err) => console.error("[CloudImport] Google libs load error:", err));
  }, [doCloudImport]);

  const showSuccessTicket = useCallback(() => {
    setIsDoneTicket(true);
    setIsVisibility3(false);
    sessionStorage.removeItem("selectedNodeList");
  }, []);

  // Effects optimalizálása
  useEffect(() => {
    if (context.getState()?.isProgress) {
      updateProgressElements();
    }
  }, [context.getState()?.isProgress, updateProgressElements]);

  useEffect(() => {
    props.setIsVisibility5?.(false);
    sessionStorage.removeItem("selectedNodeList");
  }, [props]);

  useEffect(() => {
    const { isExplorerView } = props;
    const modalName = context.getState()?.modalName;
    const isValidExplorerState = isExplorerView && modalName !== "dataimport" && modalName !== "fileupload";

    if (isValidExplorerState && progressElements.bar) {
      if (isFolderDownload) {
        setIsFolderDownload(false);
        const fileListFromFolder = filelistFromFolderRecursive(actuelleDataOwnerComponent);
        multiZipDownload(fileListFromFolder, actuelleDataOwnerComponent);
      }
      if (isZipDownload) {
        setIsZipDownload(false);
        downloadFiles();
      }
    }
  }, [progressElements.bar, props, isFolderDownload, isZipDownload, actuelleDataOwnerComponent]);
  // Graph processing effect optimalizálva
  useEffect(() => {
    if (!props.loadedGraph) return;

    const processGraph = async () => {
      try {
        let localFileList = graphProcessorToFileList(props.loadedGraph);
        console.log("ExplorerDataComponent2", localFileList);
        setFileList(localFileList);

        //originalFileListProcess();
        speedUpFileListProcess();
      } catch (error) {
        console.error("Error processing graph:", error);
      }
    };

    processGraph();
  }, [context.getState()?.isRender, storageProps.ed25519UserShare, props.loadedGraph]);

  const originalFileListProcess = async () => {
    const startTime = Date.now();

    const processedGraph = graphProcessor(props.loadedGraph);
    const dataOwnerFilesObj = processedGraph.tree;

    const [loadedContentTypeFilters, loadedTagFilters, loadedSortFilters] = await Promise.all([
      loadSelectedContentTypeFilters(),
      loadSelectedTagFilters(),
      loadSortFilter(),
    ]);

    const sortedFilteredTree = sorterAndFilter(
      dataOwnerFilesObj,
      loadedContentTypeFilters,
      loadedTagFilters,
      loadedSortFilters,
    );

    const array = [];
    treeComponentFolder(sortedFilteredTree, array, "");
    setFileList(array);

    // Content types and tags collection (if needed elsewhere)
    const contentTypesFromTree = collectContentTypesFromTree(sortedFilteredTree);
    const tagSet = collectTagsFromTree(sortedFilteredTree);

    const endTime = Date.now();
    const elapsed = endTime - startTime; // in milliseconds
    console.log(`Elapsed time: ${elapsed} ms`);
  };

  const speedUpFileListProcess = async () => {
    const startTime = Date.now();

    const processedGraph = graphProcessor(props.loadedGraph);
    const dataOwnerFilesObj = processedGraph.tree;

    const array = [];
    treeComponentFolder(dataOwnerFilesObj, array, "");
    setFileList(array);

    const endTime = Date.now();
    const elapsed = endTime - startTime; // in milliseconds
    console.log(`Elapsed time: ${elapsed} ms`);
  };

  // File operations
  const readFileTest = useCallback(
    async (node) => {
      if (!node.properties.isFile || node.properties.fileStorageId === "<link_to_file>" || !selectedAccount) {
        return;
      }

      try {
        const result = await readFileCached(
          selectedWallet?.provider,
          storageProps,
          node.properties.fileStorageId,
          node.properties?.fileSize,
        );

        const imageUrl = URL.createObjectURL(result);
        const extension = node.properties.fileStorageId.split(".").pop();
        // További feldolgozás...
      } catch (error) {
        console.error("File read error:", error);
      }
    },
    [selectedAccount, selectedWallet, storageProps, downloadLimit],
  );

  const readFileTestRespo = useCallback(
    async (node) => {
      if (
        !node.properties.isFile ||
        node.properties.fileStorageId === "<link_to_file>" ||
        !selectedAccount ||
        node.properties?.fileSize >= DOWNLOAD_LIMIT_MOBILE
      ) {
        return;
      }

      try {
        const downloadUrl = await getDownloadUrl(
          selectedWallet?.provider,
          storageProps,
          node.properties.fileName,
          node.properties.fileStorageId,
          node.properties?.fileSize,
        );
        await downloadFileIOS(downloadUrl, node.properties.fileName);
      } catch (error) {
        console.error("File download error:", error);
      }
    },
    [selectedAccount, selectedWallet, storageProps],
  );

  const downloadFileIOS = useCallback(async (downloadUrl, fileName = "download") => {
    try {
      const isIOS = isIOSDevice();

      if (isIOS && navigator.share) {
        try {
          const response = await fetch(downloadUrl);
          const blob = await response.blob();

          const file = new File([blob], fileName, {
            type: blob.type || "application/octet-stream",
          });

          await navigator.share({
            title: "Fájl letöltése",
            text: `Letöltés: ${fileName}`,
            files: [file],
          });

          return true;
        } catch (shareError) {
          console.log("Share API hiba:", shareError);
        }
      }

      // Fallback megoldás
      const response = await fetch(downloadUrl);
      const arrayBuffer = await response.arrayBuffer();

      const extension = fileName.split(".").pop()?.toLowerCase();
      const mimeType = getMimeTypeFromExtension(extension);

      const blob = new Blob([arrayBuffer], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Letöltési hiba:", error);
      // Final fallback
      window.location.assign(downloadUrl);
    }
  }, []);

  const onDownloadProgress = useCallback(
    (progressEvent) => {
      const { bar, size, timer, timertext, filenumber } = progressElements;

      if (!bar) return;

      const { totalFilesBytes, loadedFilesBytes, loaded, totalFiles, loadedFiles } = progressEvent;
      const current = loadedFilesBytes + loaded;
      const percentCompleted = (current / totalFilesBytes) * 100;
      const sizeCompleted = ((current / totalFilesBytes) * totalFilesBytes) / 1e6;

      if (bar) bar.style.width = `${percentCompleted}%`;
      const barLabel = document.getElementById("progress-bar-label");
      if (barLabel) barLabel.textContent = `${percentCompleted.toFixed(0)}%`;

      if (filenumber) {
        const currentFile = window.showDirectoryPicker ? loadedFiles : loadedFiles + 1;
        filenumber.textContent = t('files.upload.fileOfTotal', { current: currentFile, total: totalFiles });
      }

      if (size) {
        size.textContent = `${sizeCompleted.toFixed(2)}MB of ${(totalFilesBytes / 1e6).toFixed(2)}MB`;
      }

      if (progressEvent.estimated !== undefined && timer && timertext) {
        timertext.textContent = t('files.upload.timeRemaining');
        timer.textContent = formatTimeHHMMSS(progressEvent.estimated);
      }

      if (percentCompleted >= 100) {
        if (barLabel) barLabel.textContent = "Download complete!";
        setTimeout(() => {
          context.isProgress(false);
          setActuelleDataOwnerComponent(undefined);
          props.setIsMultiDownload?.(false);
        }, 100);
      }
    },
    [progressElements, context, props, t],
  );
  const downloadFiles = useCallback(async () => {
    const nodeList = getSessionStorageSelectedNodes();
    if (!nodeList) return;

    try {
      let errorFilePathList;
      if (nodeList.length === 1) {
        errorFilePathList = await singleFileDownload(nodeList[0]);
      } else {
        errorFilePathList = await multiZipDownload(nodeList);
      }

      sessionStorage.removeItem("selectedNodeList");

      if (errorFilePathList?.length > 0) {
        context.openModal("filedownloaderror", undefined, errorFilePathList);
        context.isProgress(false);
      }
    } catch (error) {
      if (isAbortError(error, props.downloadSignal)) return;
      console.error("Download error:", error);
    }
  }, [context]);

  const singleFileDownload = useCallback(
    async (node) => {
      let mainDirectoryHandle;
      if (window.showDirectoryPicker) {
        mainDirectoryHandle = await window.showDirectoryPicker({ mode: "readwrite" });
      }
      try {
        return await utilSingleFileDownload(
          selectedWallet,
          node,
          mainDirectoryHandle,
          onDownloadProgress,
          props.downloadSignal,
        );
      } catch (error) {
        if (isAbortError(error, props.downloadSignal)) return [];
        throw error;
      }
    },
    [selectedWallet, onDownloadProgress, props.downloadSignal],
  );

  const multiZipDownload = useCallback(
    async (nodeList, folderNode) => {
      console.log("nodeList", nodeList);
      console.log("folderNode", folderNode);
      if (!nodeList) return [];

      const fileList = nodeList.map((node) => ({
        path: node.properties.fileStorageId,
        size: node.properties.fileSize,
      }));

      try {
        if (window.showDirectoryPicker) {
          return await chromeDownload(
            selectedWallet?.provider,
            fileList,
            folderNode,
            onDownloadProgress,
            props.downloadSignal,
          );
        } else {
          await zipDownload(fileList, folderNode, onDownloadProgress, props.downloadSignal);
          return [];
        }
      } catch (error) {
        if (isAbortError(error, props.downloadSignal)) return [];
        context.isProgress(false);
        console.error("Multi zip download error:", error);
        return [];
      }
    },
    [selectedWallet, onDownloadProgress, context, props.downloadSignal],
  );

  const removeFileTest = useCallback(
    async (folderToRemove) => {
      if (!selectedAccount) return;

      try {
        setIsVisibility3(true);
        const selectedNodeList = getSessionStorageSelectedNodes();

        if (selectedNodeList) {
          for (const node of selectedNodeList) {
            deleteSingle2(props.loadedGraph, node.id);
            if (!folderToRemove) {
              await removeFile(storageProps, node.properties.fileStorageId);
            }
          }
        } else if (folderToRemove) {
          deleteSingle2(props.loadedGraph, folderToRemove.id);
        }

        const authToken = await loadJwt();
        await unProcessAndSaveProcessedGraphJwt(authToken, props.loadedGraph);
        showSuccessTicket();
      } catch (error) {
        console.error("File removal error:", error);
        showErrorTicket("File removal has failed!");
      }
    },
    [selectedAccount, props.loadedGraph, storageProps, showSuccessTicket, showErrorTicket],
  );

  const sorterAndFilter = useCallback(
    (origDataOwnerComponent, selectedContentTypeFiltersParam, selectedTagFiltersParam, selectedSortParam) => {
      const filtered = filterBy(origDataOwnerComponent, selectedContentTypeFiltersParam, selectedTagFiltersParam);
      return sortBy(filtered, selectedSortParam);
    },
    [],
  );

  const filterBy = useCallback((tree, selectedContentTypeFilters, selectedTagFilters) => {
    if (selectedContentTypeFilters.length === 0 && selectedTagFilters.length === 0) {
      return tree;
    }
    if (!tree) {
      return undefined;
    }

    const returningTree = { ...tree, content: [] };

    for (const child of tree.content) {
      const filteredChildResult = filterBy(child, selectedContentTypeFilters, selectedTagFilters);
      if (filteredChildResult) {
        returningTree.content.push(filteredChildResult);
      }
    }

    if (returningTree.content.length > 0) {
      return returningTree;
    }

    const contentTypeMatch =
      selectedContentTypeFilters.length === 0 || selectedContentTypeFilters.includes(tree.properties.fileMimeType);
    const tagMatch = selectedTagFilters?.length === 0 || hasIntersection(selectedTagFilters, tree.tags);

    return contentTypeMatch && tagMatch ? returningTree : undefined;
  }, []);

  const deleteFolderRecursive = useCallback(
    async (dataOwnerComponent) => {
      try {
        setIsVisibility3(true);
        await deleteNodeRecursiveJwt(props.loadedGraph, dataOwnerComponent.id);
        showSuccessTicket();
      } catch (error) {
        console.error("Delete folder error:", error);
        showErrorTicket("Delete files from folder has failed");

        if (error instanceof ReportingError) {
          dwReportError(selectedAccount, storageProps.portalClientId, error, {
            logsAndVars: error.logsAndVars,
          });
        }
      }
    },
    [props.loadedGraph, selectedAccount, storageProps, showSuccessTicket, showErrorTicket],
  );

  const filelistFromFolderRecursive = useCallback(
    (dataOwnerComponent) => {
      const childNodes = findChildrenOfNodeById2(
        props.loadedGraph,
        dataOwnerComponent.id,
        [RELATION_FILE_FOLDER2],
        undefined,
        false,
      );

      let result = [];
      for (const childNode of childNodes) {
        if (childNode._type === TYPE_FILE2) {
          result.push(childNode);
        } else if (childNode._type === TYPE_FOLDER2) {
          result = result.concat(filelistFromFolderRecursive(childNode));
        }
      }
      return result;
    },
    [props.loadedGraph],
  );

  const openNewFolderModal = useCallback(() => {
    const folders = getAllFolders2(props.loadedGraph, "/");
    context.openModal("newfolder", "", {
      folderNames: folders,
      createFolder: (parentFolderName, newFolderName) =>
        createFolder(storageProps, props.loadedGraph, parentFolderName, newFolderName),
    });
  }, [props.loadedGraph, context, storageProps]);

  const openNewFolderModalAtPath = useCallback((explorerPath) => {
    const graphPath = (explorerPath || "").replace(/^\/User Files/, "") || "/";
    context.openModal("newfolder", "", {
      fixedPath: graphPath,
      createFolder: (parentFolderName, newFolderName) =>
        createFolder(storageProps, props.loadedGraph, parentFolderName, newFolderName),
    });
  }, [props.loadedGraph, context, storageProps]);

  const createFolder = useCallback(
    async (storageProps, processedGraph, parentFolderName, newFolderName) => {
      if (!selectedAccount) return;

      try {
        setIsVisibility3(true);
        const defaultedParentFolderName = parentFolderName || "/Uploaded";
        const fileSystemRoot = findFileSystemRoot2(processedGraph);
        const parentFolderId = findNodeId2(processedGraph, defaultedParentFolderName, fileSystemRoot?.id);

        mkdirs2(processedGraph, parentFolderId, newFolderName);

        const authToken = await loadJwt();
        await unProcessAndSaveProcessedGraphJwt(authToken, processedGraph);
        setIsVisibility3(false);
        props.refreshGraph?.();
      } catch (error) {
        console.error("Folder creation error:", error);
        showErrorTicket("Folder create has failed!");
      }
    },
    [selectedAccount, props, showErrorTicket],
  );

  const treeComponentFolder = useCallback((dataOwnerComponent, array, path) => {
    const arrayLocal = array;
    let finishText = dataOwnerComponent.value?.toString();

    if (finishText === "dataOwnerFiles") {
      finishText = "Files";
    }

    const pathLocal = path === "" ? "/User Files" : path;

    const folder = {
      name: finishText,
      isDirectory: true,
      path: pathLocal,
      updatedAt: moment(dataOwnerComponent.properties.creationTime * 1000).toISOString(),
      id: dataOwnerComponent.id,
      node: dataOwnerComponent,
    };

    arrayLocal.push(folder);

    if (dataOwnerComponent.content?.length > 0) {
      for (const item of dataOwnerComponent.content) {
        if (!item.properties.isFile) {
          treeComponentFolder(item, arrayLocal, `${pathLocal}/${item.value}`);
        } else {
          const fileName = item.value ?? item.properties.fileName;
          let finishFileText = fileName;

          if (fileName?.length > FILE_NAME_MAX_LENGTH) {
            const splitArray = fileName.split(".");
            const extension = splitArray[splitArray.length - 1];
            finishFileText = `${splitArray[0].slice(0, FILE_NAME_TRUNCATE_LENGTH)}…${extension}`;
          }

          const filePath = item.properties.fileStorageId.includes("User Files")
            ? `/${item.properties.fileStorageId}`
            : `/User Files${item.properties.fileStorageId}`;

          const file = {
            name: item.properties.fileName,
            isDirectory: false,
            path: filePath,
            updatedAt: moment(item.properties.creationTime * 1000).toISOString(),
            size: item.properties.fileSize,
            id: item.id,
            node: item,
          };
          arrayLocal.push(file);
        }
      }
    }
    console.log("treeComponentFolder", arrayLocal);
  }, []);

  const onFileSelectionForDeletion = useCallback((node) => {
    const selectedNodeList = getSessionStorageSelectedNodes() || [];
    const selectedIds = selectedNodeList.map((k) => k.id);

    let updatedList;
    if (selectedIds.includes(node.id)) {
      updatedList = selectedNodeList.filter((item) => item.id !== node.id);
    } else {
      updatedList = [...selectedNodeList, node];
    }

    setSessionStorageSelectedNodes(updatedList);
  }, []);

  // Render JSX
  return (
    <>

      {true && (
        <div className="explorer-component">
          <div className="actions-button-div">
            <div className="d-flex d-lg-none graph-button" onClick={() => context.openModal("graphinfo", "", {})}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5 3C3.89543 3 3 3.89543 3 5C3 6.10457 3.89543 7 5 7C6.10457 7 7 6.10457 7 5C7 3.89543 6.10457 3 5 3ZM2 5C2 3.34315 3.34315 2 5 2C6.65685 2 8 3.34315 8 5C8 5.96218 7.54703 6.81856 6.84271 7.36754L7.77861 9.10565C8.00702 9.03693 8.2492 9 8.5 9C8.89849 9 9.2752 9.09323 9.60951 9.25907L10.4164 8.22168C10.1553 7.88378 10 7.46003 10 7C10 5.89543 10.8954 5 12 5C13.1046 5 14 5.89543 14 7C14 8.10457 13.1046 9 12 9C11.7177 9 11.449 8.9415 11.2055 8.83596L10.3985 9.87342C10.7735 10.3106 11 10.8789 11 11.5C11 12.8807 9.88071 14 8.5 14C7.11929 14 6 12.8807 6 11.5C6 10.7286 6.34934 10.0389 6.89843 9.58029L5.96256 7.84225C5.66042 7.94454 5.33669 8 5 8C3.34315 8 2 6.65685 2 5ZM12 6C11.4477 6 11 6.44772 11 7C11 7.55228 11.4477 8 12 8C12.5523 8 13 7.55228 13 7C13 6.44772 12.5523 6 12 6ZM8.5 10C7.67157 10 7 10.6716 7 11.5C7 12.3284 7.67157 13 8.5 13C9.32843 13 10 12.3284 10 11.5C10 10.6716 9.32843 10 8.5 10Z"
                  fill="#ffffff"
                />
              </svg>
              Graph view
            </div>
          </div>

          <MDBContainer style={{ maxWidth: "1936px", padding: 0 }} className="d-none d-lg-block">
            {fileList.length > 0 && (
              <FilemanagementComponentDesktop
                files={fileList}
                onFileSelectionForDeletion={onFileSelectionForDeletion}
                setIsZipDownload={setIsZipDownload}
                setIsMultiDownload={props.setIsMultiDownload}
                setActuelleDataOwnerComponent={setActuelleDataOwnerComponent}
                setIsFolderDownload={setIsFolderDownload}
                removeFileTest={removeFileTest}
                deleteFolderRecursive={deleteFolderRecursive}
                readFileTest={readFileTest}
                singleFileDownload={singleFileDownload}
                setIsWarningTicket={setIsWarningTicket}
                setIsWarningTicket3={setIsWarningTicket3}
                onCreateFolder={openNewFolderModalAtPath}
              />
            )}
          </MDBContainer>
          <div style={{ width: "100%", overflow: "hidden" }} className="d-block d-lg-none">
            {fileList.length > 0 && (
              <FilemanagementComponent
                files={fileList}
                onFileSelectionForDeletion={onFileSelectionForDeletion}
                setIsZipDownload={setIsZipDownload}
                setIsMultiDownload={props.setIsMultiDownload}
                setActuelleDataOwnerComponent={setActuelleDataOwnerComponent}
                setIsFolderDownload={setIsFolderDownload}
                removeFileTest={removeFileTest}
                deleteFolderRecursive={deleteFolderRecursive}
                readFileTestRespo={readFileTestRespo}
                singleFileDownload={singleFileDownload}
                setIsWarningTicket={setIsWarningTicket}
                setIsWarningTicket3={setIsWarningTicket3}
                onCreateFolder={openNewFolderModalAtPath}
              />
            )}
          </div>
        </div>
      )}

      <div className="ticket-div">
        {isDoneTicket && (
          <>
            <NotificationsTicket
              close={() => {
                setIsDoneTicket(false);
                props.refreshGraph?.();
              }}
              text="Deleted successfully!"
            />
            <div style={{ height: "20px" }} />
          </>
        )}
        {isWarningTicket && (
          <>
            <WarningTicket
              close={() => {
                setIsWarningTicket(false);
                setFolderToRemove(undefined);
                sessionStorage.removeItem("selectedNodeList");
              }}
              text={
                folderToRemove
                  ? "Are you sure that you want to delete the Folder?"
                  : "Are you sure that you want to delete the File(s)?"
              }
              actionsButton={true}
              onDelete={() => {
                removeFileTest(folderToRemove);
                setIsWarningTicket(false);
                setFolderToRemove(undefined);
              }}
              buttonText="Delete"
            />
            <div style={{ height: "20px" }} />
          </>
        )}
        {isWarningTicket3 && (
          <>
            <WarningTicket
              close={() => setIsWarningTicket3(false)}
              text="Are you sure that you want to delete this Folder?"
              actionsButton={true}
              onDelete={() => {
                deleteFolderRecursive(actuelleDataOwnerComponent);
                setIsWarningTicket3(false);
              }}
              buttonText="Delete"
            />
            <div style={{ height: "20px" }} />
          </>
        )}
        {isErrorTicket && (
          <>
            <ErrorTicket
              close={() => {
                setIsErrorTicket(false);
                props.refreshGraph?.();
              }}
              text={errortext}
            />
            <div style={{ height: "20px" }} />
          </>
        )}
        {showFileOperationFailedTicket && (
          <>
            <ErrorTicket
              close={() => {
                setShowFileOperationFailedTicket(false);
                props.refreshGraph?.();
              }}
              text={fileOperationFailedTicketText}
            />
            <div style={{ height: "20px" }} />
          </>
        )}
      </div>

      {isVisibility3 && <DwinityLoader />}

    </>
  );
}
